import { useCallback, useEffect, useState } from 'react';

interface UseListboxNavigationParams {
  /** Number of options currently rendered in the listbox. */
  count: number;
  /** Whether the listbox is visible; navigation is inert when closed. */
  open: boolean;
  /**
   * Identity of the current option set. The highlight re-defaults whenever this
   * changes (new query, reordered/loaded results) so a stale index never points
   * at the wrong row.
   */
  signature: string;
  /** Index highlighted on (re)open and when the option set changes. Default 0. */
  defaultIndex?: number;
  /** DOM id of the option at `index` — used for aria + scroll-into-view. */
  optionDomId: (index: number) => string;
}

export interface ListboxNavigation {
  /** Highlighted option index, or -1 when the listbox is closed/empty. */
  activeIndex: number;
  /** Value for the combobox's `aria-activedescendant` (undefined when none). */
  activeDescendantId: string | undefined;
  /** Move the highlight (e.g. from pointer hover). */
  setActiveIndex: (index: number) => void;
  /**
   * Keydown handler for ↑/↓. Returns true when it consumed the event so the
   * caller can keep its own Enter/Escape/Backspace handling for everything else.
   */
  onArrowKeys: (e: React.KeyboardEvent) => boolean;
}

/**
 * Shared ↑/↓ keyboard navigation for the autosuggest/combobox listboxes
 * (`MedicineAutosuggestField`, `ChipInputAutosuggest`). Owns the highlighted
 * index — clamping it to the option count, re-defaulting it when the option set
 * changes, and scrolling the highlighted row into view — but leaves Enter to the
 * caller, since each combobox selects differently (catalogue pick vs. chip add).
 */
export function useListboxNavigation({
  count,
  open,
  signature,
  defaultIndex = 0,
  optionDomId,
}: UseListboxNavigationParams): ListboxNavigation {
  const [index, setIndex] = useState(defaultIndex);

  // Re-default the highlight when the menu (re)opens or the option set changes.
  useEffect(() => {
    setIndex(defaultIndex);
  }, [open, signature, defaultIndex]);

  const activeIndex = open && count > 0 ? Math.min(Math.max(index, 0), count - 1) : -1;

  const scrollIntoView = useCallback(
    (i: number) => {
      const el = typeof document !== 'undefined' ? document.getElementById(optionDomId(i)) : null;
      // jsdom doesn't implement scrollIntoView, so guard the call for tests.
      el?.scrollIntoView?.({ block: 'nearest' });
    },
    [optionDomId],
  );

  const onArrowKeys = useCallback(
    (e: React.KeyboardEvent): boolean => {
      if (!open || count === 0) return false;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setIndex((i) => {
          const cur = Math.min(Math.max(i, 0), count - 1);
          const next = Math.min(count - 1, cur + 1);
          scrollIntoView(next);
          return next;
        });
        return true;
      }
      if (e.key === 'ArrowUp') {
        e.preventDefault();
        setIndex((i) => {
          const cur = Math.min(Math.max(i, 0), count - 1);
          const next = Math.max(0, cur - 1);
          scrollIntoView(next);
          return next;
        });
        return true;
      }
      return false;
    },
    [open, count, scrollIntoView],
  );

  return {
    activeIndex,
    activeDescendantId: activeIndex >= 0 ? optionDomId(activeIndex) : undefined,
    setActiveIndex: setIndex,
    onArrowKeys,
  };
}
